import type {
  DeviationEvent,
  GameRepertoireMatch,
  GameSummary,
  RepertoireEdge,
  RepertoireMatch,
} from '../api/types';
import type { PreparedMove } from '../board/review-arrows';

/**
 * Game review helpers: turn the §5.3 detector's per-ply events into the few
 * moments a player cares about, and explain each one in plain language.
 *
 * The detector is deliberately exhaustive: once a game leaves the repertoire it
 * emits a (opponent_)novelty for EVERY later ply until the game re-enters. Only
 * the first of such a run is a moment ("this is where the game left your prep").
 * A coverage gap only counts when nothing at all is saved for the position.
 */

export type DeviationKind = DeviationEvent['kind'];

export interface Moment {
  ply: number;
  kind: DeviationKind;
  event: DeviationEvent;
  /** Other events at the same ply (e.g. a coverage gap next to an opponent novelty). */
  also: DeviationEvent[];
}

const OFF_BOOK: ReadonlySet<DeviationKind> = new Set(['novelty', 'opponent_novelty']);
const PRIORITY: DeviationKind[] = [
  'suboptimal', 'novelty', 'opponent_novelty', 'coverage_gap',
  'transposition_reentry', 'transposition_entry', 'branching',
];

export function keyMoments(events: readonly DeviationEvent[]): Moment[] {
  const byPly = new Map<number, DeviationEvent[]>();
  for (const event of events) {
    const list = byPly.get(event.ply) ?? [];
    list.push(event);
    byPly.set(event.ply, list);
  }
  const offBookPlies = new Set(events.filter((e) => OFF_BOOK.has(e.kind)).map((e) => e.ply));
  const moments: Moment[] = [];
  for (const ply of [...byPly.keys()].sort((a, b) => a - b)) {
    const kept = byPly.get(ply)!.filter((e) =>
      !(OFF_BOOK.has(e.kind) && offBookPlies.has(ply - 1)) &&
      // §5.3 flags a gap when no edge has the `opponent` role, but replies saved under
      // another role are still preparation: only a position with none is a real gap.
      !(e.kind === 'coverage_gap' && e.expectedMoves.length > 0));
    if (kept.length === 0) continue;
    kept.sort((a, b) => PRIORITY.indexOf(a.kind) - PRIORITY.indexOf(b.kind));
    const [event, ...also] = kept;
    moments.push({ ply, kind: event.kind, event, also });
  }
  return moments;
}

/** Plies that were played inside the repertoire (before the first departure). */
export function bookPlies(moments: readonly Moment[], plyCount: number): number {
  const left = moments.find((m) => OFF_BOOK.has(m.kind) || m.kind === 'suboptimal');
  return left ? left.ply : plyCount;
}

/** "4." / "4…" for the move at `ply`, counted from the game's start position. */
export function moveNumberLabel(ply: number, startFen: string): string {
  const fields = startFen.trim().split(/\s+/);
  const offset = fields[1] === 'b' ? 1 : 0;
  const fullmove = Number.parseInt(fields[5] ?? '1', 10) || 1;
  const n = ply + offset;
  const number = fullmove + Math.floor(n / 2);
  return n % 2 === 0 ? `${number}.` : `${number}…`;
}

export function moveText(ply: number, startFen: string, san: string): string {
  return `${moveNumberLabel(ply, startFen)}${san}`;
}

/** Which side made the move at `ply`. */
export function moverAt(ply: number, startFen: string): 'w' | 'b' {
  const blackStarts = startFen.trim().split(/\s+/)[1] === 'b';
  return (ply % 2 === 0) !== blackStarts ? 'w' : 'b';
}

/** The repertoire's non-avoid moves from the moment's position, main line first. */
export function preparedMoves(outgoing: readonly RepertoireEdge[]): Array<PreparedMove & { san: string }> {
  const order = { main: 0, opponent: 1, alternative: 2, avoid: 3 } as const;
  return outgoing
    .filter((edge) => edge.enabled && edge.role !== 'avoid')
    .sort((a, b) => order[a.role] - order[b.role] || b.priority - a.priority)
    .map((edge) => ({ uci: edge.transition.uci, san: edge.transition.san, role: edge.role as PreparedMove['role'] }));
}

export const MOMENT_BADGES: Record<DeviationKind, string> = {
  novelty: 'You left prep',
  opponent_novelty: 'Opponent left prep',
  suboptimal: 'Move to avoid',
  branching: 'Alternative',
  transposition_entry: 'Transposition',
  transposition_reentry: 'Back in prep',
  coverage_gap: 'No reply prepared',
};

export interface Explanation {
  title: string;
  body: string;
  tone: 'miss' | 'info' | 'good';
  /** True when the position is worth adding moves to (prep ran out or was left). */
  extend: boolean;
}

export interface ExplainContext {
  startFen: string;
  sans: readonly string[];
  userColor: 'w' | 'b' | null;
  /** Prepared moves at the moment's position with SAN; empty when not a member. */
  prepared: ReadonlyArray<PreparedMove & { san: string }>;
  /** The moment where the game last left the repertoire, for re-entries. */
  leftAt?: Moment;
}

function list(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} or ${items[items.length - 1]}`;
}

export function explainMoment(moment: Moment, ctx: ExplainContext): Explanation {
  const { ply, event } = moment;
  const san = ctx.sans[ply] ?? event.actualMove;
  const played = moveText(ply, ctx.startFen, san);
  const mover = moverAt(ply, ctx.startFen);
  const you = ctx.userColor === null ? mover === 'w' ? 'White' : 'Black' : mover === ctx.userColor ? 'You' : 'Your opponent';
  const they = ctx.userColor === null ? mover === 'w' ? 'Black' : 'White' : 'your opponent';
  const prep = (roles: ReadonlyArray<PreparedMove['role']>) =>
    ctx.prepared
      .filter((move) => roles.includes(move.role) && move.uci !== event.actualMove)
      .map((move) => `${moveText(ply, ctx.startFen, move.san)}${move.role === 'alternative' ? ' (alternative)' : ''}`);

  switch (moment.kind) {
    case 'novelty': {
      const ideas = prep(['main', 'alternative']);
      return ideas.length > 0
        ? {
          title: `${you} left your preparation`,
          body: `${you} played ${played}. Your repertoire plays ${list(ideas)} here.`,
          tone: 'miss',
          extend: false,
        }
        : {
          title: 'End of your preparation',
          body: `Your repertoire has no move saved for this position, and ${you === 'You' ? 'you' : you.toLowerCase()} continued with ${played}. Add a move here to extend the line.`,
          tone: 'info',
          extend: true,
        };
    }
    case 'opponent_novelty': {
      const ideas = prep(['opponent', 'main', 'alternative']);
      const who = you === 'You' ? 'Your opponent' : you;
      return ideas.length > 0
        ? {
          title: `${who} left your preparation`,
          body: `${who} played ${played}, which your repertoire doesn't cover. You prepared for ${list(ideas)}. Add a reply to ${san} so you are ready next time.`,
          tone: 'info',
          extend: true,
        }
        : {
          title: 'End of your preparation',
          body: `Your repertoire stops here; ${who.toLowerCase()} continued with ${played}.`,
          tone: 'info',
          extend: true,
        };
    }
    case 'suboptimal': {
      const ideas = prep(['main', 'alternative']);
      const cost = event.lossCp !== null && event.lossCp !== undefined
        ? ` It loses about ${(event.lossCp / 100).toFixed(1)} pawns by engine evaluation.`
        : ' Your repertoire marks it as a move to avoid.';
      return {
        title: 'A move to avoid',
        body: `${you} played ${played}.${cost}${ideas.length ? ` Prefer ${list(ideas)}.` : ''}`,
        tone: 'miss',
        extend: false,
      };
    }
    case 'branching': {
      const main = prep(['main', 'opponent']);
      return {
        title: 'An alternative from your repertoire',
        body: `${played} is an alternative in your repertoire${main.length ? `; your main line here is ${list(main)}` : ''}.`,
        tone: 'good',
        extend: false,
      };
    }
    case 'transposition_entry':
      return {
        title: 'Transposed into your repertoire',
        body: `${played} reaches a position from your repertoire by a different move order. Your preparation applies from here.`,
        tone: 'good',
        extend: false,
      };
    case 'transposition_reentry': {
      const left = ctx.leftAt ? ` after leaving it with ${moveText(ctx.leftAt.ply, ctx.startFen, ctx.sans[ctx.leftAt.ply] ?? ctx.leftAt.event.actualMove)}` : '';
      return {
        title: 'Back in your repertoire',
        body: `The game returned to your preparation${left}: ${played} transposes into a position you have prepared.`,
        tone: 'good',
        extend: false,
      };
    }
    case 'coverage_gap':
      return {
        title: 'No reply prepared',
        body: `Your repertoire reaches this position but has no moves saved for ${they === 'your opponent' ? 'your opponent' : they}. ${played} was played. Add the likely replies so you know what to do.`,
        tone: 'info',
        extend: true,
      };
    default:
      return { title: 'Repertoire moment', body: `${played} was played.`, tone: 'info', extend: false };
  }
}

/** One-line summary of how long the game stayed in the repertoire. */
export function bookSummary(
  moments: readonly Moment[],
  sans: readonly string[],
  startFen: string,
  userColor: 'w' | 'b' | null,
): string {
  const left = moments.find((m) => OFF_BOOK.has(m.kind) || m.kind === 'suboptimal');
  if (!left) {
    return sans.length === 0 ? 'No moves to review.' : 'The whole game stayed inside your preparation.';
  }
  const move = moveText(left.ply, startFen, sans[left.ply] ?? left.event.actualMove);
  if (left.ply === 0) return `The game left your preparation on the first move, ${move}.`;
  if (userColor === null) return `The game followed your preparation until ${move}.`;
  const who = moverAt(left.ply, startFen) === userColor ? 'you' : 'your opponent';
  return `The game followed your preparation until ${who} played ${move}.`;
}

/** A match worth reviewing against: the game actually spent moves in the repertoire. */
export function isRelevantMatch(match: RepertoireMatch): boolean {
  return match.bookPlies > 0 || match.matchedPlies > 0;
}

/**
 * Which game and repertoire to open without asking. An explicit choice wins when it
 * is compatible with the game; otherwise the most recent game that followed a
 * repertoire (the preferred one first, when given), compared with its best match.
 */
export function pickReviewTarget(
  games: readonly GameSummary[],
  matches: ReadonlyMap<string, GameRepertoireMatch>,
  wanted: { gameId?: string | null; repertoireId?: string | null } = {},
): { gameId: string | null; repertoireId: string | null } {
  const relevant = (gameId: string) => (matches.get(gameId)?.matches ?? []).filter(isRelevantMatch);
  let gameId = wanted.gameId && games.some((g) => g.gameId === wanted.gameId) ? wanted.gameId : null;
  if (gameId === null && wanted.repertoireId) {
    gameId = games.find((g) => relevant(g.gameId).some((m) => m.repertoireId === wanted.repertoireId))?.gameId ?? null;
  }
  gameId ??= games.find((g) => relevant(g.gameId).length > 0)?.gameId ?? games[0]?.gameId ?? null;
  if (gameId === null) return { gameId: null, repertoireId: null };
  const candidates = matches.get(gameId)?.matches ?? [];
  const explicit = candidates.find((m) => m.repertoireId === wanted.repertoireId);
  const best = relevant(gameId)[0];
  return { gameId, repertoireId: explicit?.repertoireId ?? best?.repertoireId ?? null };
}
